import React, { useCallback, useEffect, useMemo, useState } from "react";

import "./WealthSeriesOverview.scss";
import { useLocation, useParams } from "react-router-dom";
import { connect } from "react-redux";
import AtlasLight from "../../assets/images/wealth-series/overview/atlas-light.png";
import AtlasDark from "../../assets/images/wealth-series/overview/atlas-dark.png";
import TitanLight from "../../assets/images/wealth-series/overview/titan-light.png";
import TitanDark from "../../assets/images/wealth-series/overview/titan-dark.png";
import EverestLight from "../../assets/images/wealth-series/overview/everest-light.png";
import EverestDark from "../../assets/images/wealth-series/overview/everest-dark.png";
import CoreLight from "../../assets/images/wealth-series/core-logo.png";
import CoreDark from "../../assets/images/wealth-series/core-logo-dark.png";
import { portfoliosData, urlToNameMap } from "./portfoliosData";
import WSPortfolioItem from "./components/WSPortfolioItem/WSPortfolioItem";
import { Tab, Tabs, Chip, Tooltip } from "@mui/material";
import WSPortfolioActivities from "./components/WSPortfolioActivities/WSPortfolioActivities";
import WSGuidedRebalancing from "./components/WSGuidedRebalancing/WSGuidedRebalancing";
import WSGRModal from "./components/WSGuidedRebalancing/WSGRModal/WSGRModal";
import { wealthGuidedRebalancesGet } from "../../appRedux/ducks/wealthGuidedRebalancesGet";
import WSTelegramCTA from "./components/WSTelegramCTA/WSTelegramCTA";
import WSLiveStream from "./components/WSLiveStream/WSLiveStream";
import WSBookMentorship from "./components/WSBookMentorship/WSBookMentorship";
import { portfoliosByTierLoad } from "../../appRedux/ducks/wealthPortfolio";
import { useMedia } from "react-media";
import moment from "moment";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import { fetchSubscribedPortfoliosRequest } from "../../appRedux/ducks/portfolioSubscription";
import { dxFeedSocketListen } from "../../appRedux/ducks/dxFeedSocket";
import WSContactSupport from "./components/WSContactSupport/WSContactSupport";
import AutoTradingAlert from "../common/AutoTradingAlert/AutoTradingAlert";
import "moment-timezone";
import { useAuth } from "../../contexts/Auth";

const mediaQuery = {
  isMobile: "screen and (max-width: 991px)",
};

const WealthSeriesOverview = ({
  themeMode,
  fetchPortfoliosByTier,
  wealthPortfolios,
  loadingPortfolio,
  fetchSubscribed,
  listen,
  wealthGuidedRebalancesGet,
  fetchGuidedRebalances,
}) => {
  const { series } = useParams();
  const location = useLocation();
  const {
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  } = useAuth();

  const { isMobile } = useMedia({
    queries: mediaQuery,
  });
  // ?tab=livestream deep-links straight to the Livestream tab (value 2) —
  // used by the Settings livestream calendar cards.
  const [value, setValue] = useState(() =>
    new URLSearchParams(location.search).get("tab") === "livestream"
      ? 2
      : isMobile
        ? 0
        : 1,
  );
  const [isGuidedRebalancingFullPage, setIsGuidedRebalancingFullPage] =
    useState(false);
  const [guidedRebalancingStep, setGuidedRebalancingStep] = useState(0);
  const [showGuidedRebalancingModal, setShowGuidedRebalancingModal] =
    useState(false);
  const [pendingTabValue, setPendingTabValue] = useState(null);
  const [hasCheckedGuidedRebalances, setHasCheckedGuidedRebalances] =
    useState(false);

  const handleChange = useCallback(
    (_, newValue) => {
      // If we're currently on guided rebalancing and on step 1, show modal instead of navigating
      if (value === 4 && guidedRebalancingStep === 1 && newValue !== 4) {
        setPendingTabValue(newValue);
        setShowGuidedRebalancingModal(true);
        return;
      }

      // Check if we're switching to or from Guided Rebalancing
      if (newValue === 4) {
        setIsGuidedRebalancingFullPage(true);
      } else if (value === 4) {
        setIsGuidedRebalancingFullPage(false);
      }
      setValue(newValue);
    },
    [value, guidedRebalancingStep],
  );

  useEffect(() => {
    fetchSubscribed();
  }, []);

  // Same-route navigations (e.g. switching series while already on a wealth
  // series page) don't remount, so honor the livestream deep link on query
  // changes too.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("tab") === "livestream") {
      setValue(2);
    }
  }, [location.search]);

  // Fetch guided rebalances when series is available or changes
  useEffect(() => {
    if (series && !hasCheckedGuidedRebalances) {
      fetchGuidedRebalances(series);
      setHasCheckedGuidedRebalances(true);
    }
  }, [series, hasCheckedGuidedRebalances, fetchGuidedRebalances]);

  // Handle guided rebalancing modal exit
  const handleGuidedRebalancingModalExit = () => {
    setShowGuidedRebalancingModal(false);
    if (pendingTabValue !== null) {
      setValue(pendingTabValue === 1 ? 1 : pendingTabValue); // Portfolio Activity is value 1
      setIsGuidedRebalancingFullPage(false);
      setPendingTabValue(null);
    }
  };

  // Handle direct exit to portfolio activity
  const handleExitToPortfolioActivity = () => {
    setValue(1); // Portfolio Activity tab
    setIsGuidedRebalancingFullPage(false);
    setGuidedRebalancingStep(0);
  };

  // Reset guided rebalancing check when series changes
  useEffect(() => {
    setHasCheckedGuidedRebalances(false);
    setGuidedRebalancingStep(0);
  }, [series]);

  // Helper function to determine if a portfolio key matches the current series
  // and handles both auto and regular portfolios
  const matchesCurrentSeries = useCallback(
    (key) => {
      const baseKey = key.replace("_auto", "");
      return baseKey.includes(series);
    },
    [series],
  );

  // Determine if we're using auto portfolios based on the user's permissions
  const isUsingAutoPortfolios = useMemo(() => {
    if (series === "atlas") return hasWealthSeriesAtlasAuto;
    if (series === "titan") return hasWealthSeriesTitanAuto;
    if (series === "everest") return hasWealthSeriesEverestAuto;
    return false;
  }, [
    series,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);

  // Check if any auto portfolios are actually in use
  const hasAutoPortfoliosLoaded = useMemo(() => {
    return (
      Object.keys(wealthPortfolios).filter(
        (key) => key.includes("_auto") && matchesCurrentSeries(key),
      ).length > 0
    );
  }, [wealthPortfolios, matchesCurrentSeries]);

  // Combined flag to determine if we should show auto portfolio indicators
  const showAutoPortfolioIndicators = useMemo(() => {
    return isUsingAutoPortfolios && hasAutoPortfoliosLoaded;
  }, [isUsingAutoPortfolios, hasAutoPortfoliosLoaded]);

  const last_updated = useMemo(() => {
    const filteredLastUpdatedDates = Object.keys(wealthPortfolios)
      .filter(matchesCurrentSeries)
      .map((key) => wealthPortfolios[key]?.last_updated)
      .filter((date) => date && moment(date).isValid());
    // Find the newest (most recent) last_updated date
    let newestDate = null;
    if (filteredLastUpdatedDates.length > 0) {
      newestDate = filteredLastUpdatedDates.reduce((latest, current) => {
        return moment(current).isAfter(latest) ? current : latest;
      }, filteredLastUpdatedDates[0]);
    }
    return newestDate;
  }, [wealthPortfolios, series, matchesCurrentSeries]);

  const tickers = useMemo(() => {
    let allocationTickers = new Set();
    if (series !== "everest") {
      allocationTickers = new Set(
        Object.keys(wealthPortfolios)
          .filter(matchesCurrentSeries)
          .flatMap((key) =>
            wealthPortfolios[key]?.allocations
              .sort((a, b) => b.ticker_weightage - a.ticker_weightage)
              .slice(0, 5),
          )
          .map((allocation) => allocation.ticker),
      );
    }

    const adjustmentTickers = new Set(
      Object.keys(wealthPortfolios)
        .filter(matchesCurrentSeries)
        .flatMap((portfolioKey) => {
          const portfolio = wealthPortfolios[portfolioKey];
          return (portfolio?.adjustments || []).flatMap((adj) =>
            Object.keys(adj.adjustments || {}),
          );
        }),
    );

    return new Set([...allocationTickers, ...adjustmentTickers]);
  }, [wealthPortfolios, series, matchesCurrentSeries]);

  useEffect(() => {
    if (series !== "everest") {
      listen([...tickers]);
    }
    return () => listen([]);
  }, [tickers, series, listen]);

  const tierLogo = useMemo(() => {
    if (series === "atlas") {
      return themeMode === "light" ? AtlasLight : AtlasDark;
    } else if (series === "titan") {
      return themeMode === "light" ? TitanLight : TitanDark;
    } else if (series === "everest") {
      return themeMode === "light" ? EverestLight : EverestDark;
    } else if (series === "core") {
      return themeMode === "light" ? CoreLight : CoreDark;
    }
  }, [series, themeMode]);

  // Let the backend handle whether to return auto portfolios
  useEffect(() => {
    fetchPortfoliosByTier(series, !series.includes("everest"));
  }, [series, fetchPortfoliosByTier]);

  const portfolioArr = useMemo(() => {
    return portfoliosData[series].portfolios;
  }, [series]);

  const telegramLink = useMemo(() => {
    if (series === "atlas") {
      return "https://t.me/+1YqP9aQLDJwzOTgx";
    } else if (series === "titan") {
      return "https://t.me/+l1VOlX9fNHE0NDQx";
    } else if (series === "everest") {
      return "https://t.me/+Uh2s1YztDcM2Nzgx";
    } else if (series === "core") {
      return "https://t.me/+_9QxZXcB2ZdiZDI5";
    }
  }, [series]);

  const subTitle = useMemo(() => {
    if (series === "atlas") {
      return "Atlas Livestream";
    } else if (series === "titan") {
      return "Titan Livestream";
    } else if (series === "everest") {
      return "Everest Livestream";
    } else if (series === "core") {
      return "Core Livestream";
    }
  }, [series]);

  const scheduleDate = useMemo(() => {
    if (series === "atlas") {
      return "Every Monday 4:30pm EST";
    } else if (series === "titan") {
      return "Every Tuesday 4:30pm EST";
    } else if (series === "everest") {
      return "Every Wednesday 4:30pm EST";
    } else if (series === "core") {
      return "Every Thursday 2:30pm EST";
    }
  }, [series]);

  const description = useMemo(() => {
    if (series === "atlas") {
      return "Eric Armenteros and Dane Glisek will conduct a weekly walkthrough of our Atlas portfolios and reveal the latest insights on the market.";
    } else if (series === "titan") {
      return "Eric Armenteros and Dane Glisek will conduct a weekly walkthrough of our Titan portfolios, describing how to achieve maximum returns with minimal drawdowns through smart diversification.";
    } else if (series === "everest") {
      return "Eric Armenteros and Dane Glisek will conduct a weekly walkthrough of our Everest portfolios, with advanced insights on wealth management using hedge fund strategies and smart diversification.";
    } else if (series === "core") {
      return "Eric Armenteros and Dane Glisek will conduct a weekly walkthrough of our Core portfolios and reveal the latest insights on the market.";
    }
  }, [series]);

  const vimeoEventID = useMemo(() => {
    if (series === "atlas") {
      return "4788503";
    } else if (series === "titan") {
      return "4788514";
    } else if (series === "everest") {
      return "4788513";
    } else if (series === "core") {
      return "5399559";
    }
  }, [series]);

  // scroll to top smoothly on component rendering
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [series]);

  useEffect(() => {
    if (!isMobile && value === 0) {
      setValue(1);
    }
  }, [isMobile]);

  // Get adjustments considering both auto and regular portfolios
  const { adjustments } = useMemo(() => {
    const transformed = Object.keys(wealthPortfolios)
      .filter(matchesCurrentSeries)
      .flatMap((portfolioKey) => {
        const portfolio = wealthPortfolios[portfolioKey];
        // Use base portfolio name without _auto suffix for display purposes
        const portfolioName = (portfolio.name || "")
          .replaceAll(" ", "-")
          .replace("_auto", "");

        return (portfolio?.adjustments || []).flatMap((adj) => {
          const date = adj?.rebalance_date?.split("T")[0];
          if (!date || !adj.adjustments) return [];
          // Convert the ticker adjustments object into an array of objects
          return Object.entries(adj.adjustments).map(
            ([
              ticker,
              {
                old,
                new: newPrice,
                diff,
                name,
                last_price,
                ai_rebalance_price,
                sector,
              },
            ]) => ({
              date,
              ticker,
              oldPrice: old,
              newPrice,
              diff,
              portfolioName: urlToNameMap[portfolioName] || portfolioName,
              name,
              last_price,
              portfolioKey: portfolioName,
              ai_entry_price: ai_rebalance_price,
              sector,
            }),
          );
        });
      })

      .reduce((acc, item) => {
        // Group by date
        if (!acc[item.date]) {
          acc[item.date] = [];
        }
        acc[item.date].push({
          ticker: item.ticker,
          diff: item.diff,
          newPrice: item.newPrice,
          oldPrice: item.oldPrice,
          portfolioName: item.portfolioName,
          name: item.name,
          last_price: item.last_price,
          portfolioKey: item.portfolioKey,
          ai_entry_price: item.ai_entry_price,
          sector: item?.sector,
        });
        return acc;
      }, {});

    const result = Object.keys(transformed).map((date) => ({
      date,
      adjustments: transformed[date],
    }));
    return { adjustments: result };
  }, [series, wealthPortfolios, matchesCurrentSeries]);

  const autoTooltipText =
    "View your portfolio performance or trades accurately in Orion. Your account details are available in your email from our RIA.";

  return (
    <div className="ws-overview">
      {/* Use the shared AutoTradingAlert component */}
      <AutoTradingAlert
        showAutoPortfolioIndicators={showAutoPortfolioIndicators}
        broker="Orion"
      />

      {/* Show header and portfolios only when not in guided rebalancing full page mode */}
      {!isGuidedRebalancingFullPage && (
        <>
          <div className="ws-overview__header">
            <div className="ws-overview__logo-container">
              <img src={tierLogo} alt="logo" className="ws-overview__logo" />
              {showAutoPortfolioIndicators && (
                <Tooltip
                  title={autoTooltipText}
                  arrow
                  placement="bottom"
                  className="auto-tooltip"
                >
                  <Chip label="Auto" size="small" className="auto-chip" />
                </Tooltip>
              )}
            </div>
            <div className="ws-overview__header-text">
              {loadingPortfolio ? (
                <BeautifulSkeleton width={350} height={20} />
              ) : (
                <>
                  Last updated: &nbsp;
                  {moment(last_updated)
                    .tz("America/New_York")
                    .format("dddd, MMM D, YYYY h:mm A")}{" "}
                  (EST)
                </>
              )}
            </div>
            {isMobile && <WSContactSupport isMobile />}
          </div>

          {!isMobile && (
            <>
              <div className="ws-overview-portfolios">
                {portfolioArr?.map((portfolio, index) => (
                  <div
                    key={index}
                    className={`ws-overview-portfolio ${series === "everest" ? "ws-overview-portfolio--everest" : ""}`}
                  >
                    <WSPortfolioItem portfolio={portfolio} type={series} />
                  </div>
                ))}
              </div>
              <div className="simulated">*Results are simulated</div>
            </>
          )}
        </>
      )}

      {/* Always show tabs */}
      {isMobile && (
        <>
          <div className="ws-overview__tabs">
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              className="w-full ws-portfolio-ticker-info-tabs ws-overview-tabs--mobile"
            >
              <Tab label="Portfolios" value={0} />
              <Tab label="Guided Rebalancing" value={4} />
              <Tab label="Portfolio Activity" value={1} />
              <Tab label="Livestream" value={2} />
              <Tab label="Book Mentorship Session" value={3} />
            </Tabs>
          </div>

          {!isGuidedRebalancingFullPage && (
            <div className="w-full ws-mobile-telegram-cta">
              <WSTelegramCTA
                link={telegramLink}
                className="ws-telegram-mobile"
              />
            </div>
          )}
        </>
      )}

      {!isMobile && (
        <div className="ws-overview__tabs">
          <Tabs
            value={value}
            onChange={handleChange}
            className="w-full ws-portfolio-ticker-info-tabs"
          >
            <Tab label="Portfolio Activity" value={1} />
            <Tab label="Guided Rebalancing" value={4} />
            <Tab label="Livestream" value={2} />
            <Tab label="Book Mentorship Session" value={3} />
          </Tabs>

          {!isGuidedRebalancingFullPage && (
            <>
              <WSTelegramCTA link={telegramLink} />
              <WSContactSupport />
            </>
          )}
        </div>
      )}

      {/* Tab content */}
      <div className="ws-overview__tabs-content">
        {value === 0 && isMobile && (
          <div className="ws-overview-portfolios ws-overview-portfolios--mobile">
            {portfolioArr?.map((portfolio, index) => (
              <div
                key={index}
                className={`ws-overview-portfolio ${series === "everest" ? "ws-overview-portfolio--everest" : ""}`}
              >
                <WSPortfolioItem portfolio={portfolio} type={series} />
              </div>
            ))}
            <div className="simulated">*Results are simulated </div>
          </div>
        )}
        {value === 1 && (
          <WSPortfolioActivities
            adjustments={adjustments}
            loading={loadingPortfolio}
            currentPortfolio={series}
          />
        )}
        {value === 2 && (
          <WSLiveStream
            subTitle={subTitle}
            scheduleDate={scheduleDate}
            description={description}
            eventID={vimeoEventID}
            goBookMentorship={() => setValue(3)}
          />
        )}
        {value === 3 && (
          <WSBookMentorship tier={series} themeMode={themeMode} />
        )}
        {value === 4 && (
          <div className="ws-guided-rebalancing-fullpage">
            <WSGuidedRebalancing
              tier={series}
              themeMode={themeMode}
              onExitToPortfolioActivity={handleExitToPortfolioActivity}
              onStepChange={setGuidedRebalancingStep}
              wealthGuidedRebalancesGet={wealthGuidedRebalancesGet}
              hasCheckedExisting={hasCheckedGuidedRebalances}
            />
          </div>
        )}
      </div>

      {/* Guided Rebalancing Exit Modal */}
      <WSGRModal
        open={showGuidedRebalancingModal}
        onClose={() => setShowGuidedRebalancingModal(false)}
        onExit={handleGuidedRebalancingModalExit}
      />
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
  wealthGuidedRebalancesGet: state.wealthGuidedRebalancesGet,
});

const dispatchToProps = (dispatch) => ({
  fetchPortfoliosByTier: (tier, addSparkline, forceRefresh = false) =>
    dispatch(portfoliosByTierLoad(tier, addSparkline, forceRefresh)),
  fetchSubscribed: () => dispatch(fetchSubscribedPortfoliosRequest()),
  listen: (symbols, options, disconnect, risk) =>
    dispatch(dxFeedSocketListen(symbols, options, disconnect, risk)),
  fetchGuidedRebalances: (tier) => dispatch(wealthGuidedRebalancesGet(tier)),
});

export default connect(stateToProps, dispatchToProps)(WealthSeriesOverview);
